import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  SelectPicker,
  IconButton,
  Tree,
  Tabs,
  Input,
  Table,
  Message,
  Progress,
  Pagination,
  Tooltip,
  Whisper,
  InputGroup,
  Modal,
  Button,
  useToaster,
} from "rsuite";
import { Tabs as AntTabs } from "antd";
import EditIcon from "@rsuite/icons/Edit";
import TableIcon from "@rsuite/icons/Table";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import PlayOutlineIcon from "@rsuite/icons/PlayOutline";
import SaveIcon from "@rsuite/icons/Save";
import "./QueryEditorPage.css";
import * as util from "../../util/QueryEditorPageUtil/QueryEditorPageUtil";
import { Editor } from "@monaco-editor/react";
import { SidebarLeft } from "../../assets/images/sidebarLeft";
import { SidebarRight } from "../../assets/images/sidebarRight.js";
import { SidebarDown } from "../../assets/images/sidebarDown.js";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import CloseIcon from "@rsuite/icons/Close";
import { Allotment } from "allotment";
import "allotment/dist/style.css";
import ExportButton from "../../util/ExportButton/ExportButton";
import { saveUserSession } from "../../api";
import QueryTree from "../../util/QueryTree/QueryTree.js";
import QueryHistoryPanel from "../../util/QueryHistoryPanel/QueryHistoryPanel.js";
import CopyIcon from "@rsuite/icons/Copy";
import { useTheme } from "../../context/ThemeContext";

const { Column, HeaderCell, Cell } = Table;
export const getTooltipText = () => {
  const platform = navigator.platform.toLowerCase();
  if (platform.includes("mac")) {
    return "Press ⌘ + R to run";
  } else if (platform.includes("win") || platform.includes("linux")) {
    return "Press Ctrl + R to run";
  } else {
    return "Press Cmd/Ctrl + R to run";
  }
};
export const tableTooltipText = () => {
  return "Double-click for Table Details";
};
const TECHSPOT_URL = "https://techspot.comcast.com/techspot?id=techspot_submit_ticket_v2";

const WafSupportId = ({ supportId }) => {
  const copyToClipboard = () => navigator.clipboard.writeText(supportId);
  return (
    <span className="waf-support-id">
      <code className="waf-support-id-code">{supportId}</code>
      <IconButton appearance="subtle" size="xs" icon={<CopyIcon />} onClick={copyToClipboard} />
    </span>
  );
};

const WafErrorContent = ({ message }) => {
  const supportIdMatch = message.match(/Support ID: (\d+)/);
  const supportId = supportIdMatch ? supportIdMatch[1] : null;

  return (
    <div className="waf-error-content">
      {message.split("\n").map((line, i) => (
        <p key={i} className="waf-error-line">
          {line.includes("Support ID:") && supportId ? (
            <>
              Support ID: <WafSupportId supportId={supportId} />
            </>
          ) : line.includes("{{TECHSPOT_LINK}}") ? (
            <>
              {line.split("{{TECHSPOT_LINK}}")[0]}
              <a href={TECHSPOT_URL} target="_blank" rel="noopener noreferrer">Techspot Ticket Form</a>
              {line.split("{{TECHSPOT_LINK}}")[1]}
            </>
          ) : (
            line
          )}
        </p>
      ))}
    </div>
  );
};

const isWafError = (msg) => msg && msg.includes("web application firewall (WAF)");

export const ErrorMessage = ({ queryResult, error, onClose }) => (
  <div className="query-result-error">
    <Message showIcon type="error" header="Error" className="error-message-box">
      <div className="error-header">
        <span>Error occurred while executing query</span>
        <CloseIcon
          className="close-error-icon"
          onClick={onClose}
          data-testid="close-error-message"
        />
      </div>
      <div className="error-content">
        <div>
          <div className="error-section-header">
            <strong>ERROR:</strong>
            <IconButton
              appearance="subtle"
              size="xs"
              icon={<CopyIcon />}
              className="error-copy-btn"
              onClick={() => {
                const errorMessage = queryResult?.error || error || "Unknown error occurred";
                const displayMessage = errorMessage.split(" Exception: ")[1] || errorMessage;
                navigator.clipboard.writeText(displayMessage);
              }}
            />
          </div>
          {(() => {
            const errorMessage = queryResult?.error || error || "Unknown error occurred";
            const displayMessage = errorMessage.split(" Exception: ")[1] || errorMessage;
            if (isWafError(displayMessage)) {
              return <WafErrorContent message={displayMessage} />;
            }
            return <pre className="error-text">{displayMessage}</pre>;
          })()}
          {(queryResult?.error?.includes("Database") || queryResult?.error?.includes("Table")) &&
            queryResult?.error?.includes("does not exist") && (
              <div className="error-hint">
                💡 Please ensure that you have selected the correct database in the editor panel and referenced the correct table in your query.
              </div>
            )}
          {queryResult?.sql && (
            <div className="error-section-header">
              <strong>SQL:</strong>
              <IconButton
                appearance="subtle"
                size="xs"
                icon={<CopyIcon />}
                className="error-copy-btn"
                onClick={() => navigator.clipboard.writeText(queryResult.sql)}
              />
              <pre className="error-sql">
                {queryResult.sql}
              </pre>
            </div>
          )}
        </div>
      </div>
    </Message>
  </div>
);

const QueryEditorPage = ({ setHasTabsWithQueries }) => {
  const { isDarkMode } = useTheme();
  const lastLoadedDatasourceRef = useRef(null);
  const [isToggled, setIsToggled] = useState(true);
  const [isRightToggled, setIsRightToggled] = useState(false);
  const [datasources, setDatasources] = useState([]);
  const [tableTrees, setTableTrees] = useState([]);
  const [datasourceTablesMap, setDatasourceTablesMap] = useState({});
  const datasourceTablesMapRef = useRef({});
  const [tablesCount, setTablesCount] = useState(0);

  const defaultTabs = useMemo(
    () => [{ key: "1", title: "Untitled Editor 1", content: "", datasource: null }],
    []
  );

  const persistedState = useMemo(() => util.readQueryEditorState(), []);

  const [tabs, setTabs] = useState(() => {
    return Array.isArray(persistedState?.tabs) && persistedState.tabs.length > 0
      ? persistedState.tabs
      : defaultTabs;
  });

  const [activeKey, setActiveKey] = useState(() => {
    const initialTabs =
      Array.isArray(persistedState?.tabs) && persistedState.tabs.length > 0
        ? persistedState.tabs
        : defaultTabs;
    const persistedActiveKey =
      typeof persistedState?.activeKey === "string" ? persistedState.activeKey : null;
    return persistedActiveKey && initialTabs.some((t) => t.key === persistedActiveKey)
      ? persistedActiveKey
      : initialTabs[0].key;
  });

  const [selectedDatasource, setSelectedDatasource] = useState(() => {
    return persistedState?.selectedDatasource ?? null;
  });

  const [dictionaries, setDictionaries] = useState([]);
  const editorPanelDatasourceRef = useRef(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedTableDatasource, setSelectedTableDatasource] = useState(null);
  const clickTimeout = useRef(null);
  const [result, setResult] = useState(null);
  const [results, setResults] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState(null);
  const [resultTabPagination, setResultTabPagination] = useState({});
  const [isMultiQuery, setIsMultiQuery] = useState(false);
  
  const [error, setError] = useState(null);
  const [editorHeight, setEditorHeight] = useState(window.innerHeight - 124);
  const [loading, setLoading] = useState(false);
  const [queryHistoryLoading, setQueryHistoryLoading] = useState(false);
  const [resultStats, setResultStats] = useState(null);
  const [activeIconLeft, setActiveIconLeft] = useState(null);
  const [activeIconRight, setActiveIconRight] = useState(null);
  const [activeIconDown, setActiveIconDown] = useState(null);
  const [resultsHeight, setResultsHeight] = useState(
    (window.innerHeight * 25) / 100
  );
  const [resultColumnWidths, setResultColumnWidths] = useState({});
  const [isRotated, setIsRotated] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cancelTokenSource, setCancelTokenSource] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [accessDS, setAccessDS] = useState([]);
  const [noAccessDS, setNoAccessDS] = useState([]);
  const [editorContent, setEditorContent] = useState("");
  const editorRefs = useRef({});
  const [queryHistoryOpen, setQueryHistoryOpen] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCloseTabModal, setShowCloseTabModal] = useState(false);
  const [tabToCloseKey, setTabToCloseKey] = useState(null);
  const toaster = useToaster();
  const queryHistoryRef = useRef([]);
  const refreshSavedQueriesRef = useRef(null);
  const [editorPanelDatasource, setEditorPanelDatasource] = useState(() => {
    return persistedState?.editorPanelDatasource ?? null;
  });
  const [hasUserClearedDatasource, setHasUserClearedDatasource] = useState(false);
  const userId = util.getUserId();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [isResultVisible, setIsResultVisible] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(275);
  const isResizing = useRef(false);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current) return;
    const newWidth = Math.min(Math.max(e.clientX - 16, 275), 475);
    setSidebarWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const initialize = async () => {
      await util.fetchDatasources(setDatasources, setNoAccessDS, setAccessDS);
    };
    initialize();
  }, []);

  useEffect(() => {
    util.writeQueryEditorState({
      tabs,
      activeKey,
      selectedDatasource,
      editorPanelDatasource,
    });
  }, [tabs, activeKey, selectedDatasource, editorPanelDatasource]);

  useEffect(() => {
    if (datasources.length > 0 && !selectedDatasource && !hasUserClearedDatasource && accessDS) {
      console.log("Access DS:", accessDS);
      console.log("Available datasources:", datasources.map(ds => ds.value));
      const storedPreference = localStorage.getItem(`maple_preferred_ds_${userId}`);
      console.log("Stored preferred datasource:", storedPreference);
      const isValidPreference = storedPreference &&
        datasources.some(ds => ds.value === storedPreference) &&
        !(Array.isArray(noAccessDS) && noAccessDS.includes(storedPreference));
      const firstDatasource = isValidPreference ? storedPreference : accessDS;
      setSelectedDatasource(firstDatasource);
      setEditorPanelDatasource(firstDatasource);
      
      const isQueryEditorPanel = false;
      util.handleDatasourceSelect(
        firstDatasource,
        setTableTrees,
        setSelectedDatasource,
        setEditorPanelDatasource,
        isQueryEditorPanel,
        setTablesCount,
        progress,
        setProgress,
        tabs,
        setTabs,
        activeKey,
        setDictionaries,
        setDatasourceTablesMap
      );
    }
  }, [accessDS]);

  useEffect(() => {
    if (!selectedDatasource || hasUserClearedDatasource) return;
    if (datasources.length === 0) return;
    if (lastLoadedDatasourceRef.current === selectedDatasource) return;

    lastLoadedDatasourceRef.current = selectedDatasource;

    const isQueryEditorPanel = false;
    util.handleDatasourceSelect(
      selectedDatasource,
      setTableTrees,
      setSelectedDatasource,
      setEditorPanelDatasource,
      isQueryEditorPanel,
      setTablesCount,
      progress,
      setProgress,
      tabs,
      setTabs,
      activeKey,
      setDictionaries,
      setDatasourceTablesMap
    );
  }, [datasources.length, selectedDatasource, hasUserClearedDatasource, progress, tabs, activeKey]);

  useEffect(() => {
    if (editorPanelDatasource) {
      const isQueryEditorPanel = true;
      util.handleDatasourceSelect(
        editorPanelDatasource,
        setTableTrees,
        setSelectedDatasource,
        setEditorPanelDatasource,
        isQueryEditorPanel,
        setTablesCount,
        progress,
        setProgress,
        tabs,
        setTabs,
        activeKey,
        setDictionaries,
        setDatasourceTablesMap
      );
    }
  }, [editorPanelDatasource]);

  useEffect(() => {
    util.fetchQueryHistory(queryHistoryRef);
  }, []);

  useEffect(() => {
    editorPanelDatasourceRef.current = editorPanelDatasource;
  }, [editorPanelDatasource]);

  useEffect(() => {
    datasourceTablesMapRef.current = datasourceTablesMap;
  }, [datasourceTablesMap]);

  useEffect(() => {
  
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.key === activeKey ? { ...tab, datasource: editorPanelDatasource } : tab
      )
    );
  }, [editorPanelDatasource, activeKey, setTabs]);

    // Initialize pagination and multi-query flag when new results arrive
  useEffect(() => {
    if (results && results.length > 0) {
      setIsResultVisible(true);
      setIsRotated(true);
      setActiveIconDown("down");
      if (results.length > 1) {
        setIsMultiQuery(true);
      }
      
      setCurrentPage(1);
      setRowsPerPage(30);
      
      setResultTabPagination(prevPagination => {
        const newPagination = {};
        
        results.forEach(result => {
          if (result.__id) {
            newPagination[result.__id] = { page: 1, limit: 30 };
          }
        });
        
        return newPagination;
      });
    } else if (!results) {
      setIsMultiQuery(false);
      setIsResultVisible(true);
      setIsRotated(false);
      setActiveIconDown(null);
    }
  }, [results]);

  useEffect(() => {
    const autoSaveTimeouts = new Map();

    tabs.forEach((tab) => {
      if (
        tab.isSavedQuery &&
        tab.content &&
        (tab.content !== tab.existingContent || tab.datasource !== tab.existingDatasource) &&
        tab.datasource
      ) {

        if (autoSaveTimeouts.has(tab.key)) {
          clearTimeout(autoSaveTimeouts.get(tab.key));
        }

        const timeoutId = setTimeout(() => {
          util.saveQuery(tab, setTabs).then(() => {
            if (refreshSavedQueriesRef.current) {
              refreshSavedQueriesRef.current();
            }
          });
        }, 2000);

        autoSaveTimeouts.set(tab.key, timeoutId);
      }
    });

    return () => {
      autoSaveTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [tabs]);

  // Calculate column widths only for the active tab and cache them
  useEffect(() => {
    if (results && activeResultTab) {
      if (!resultColumnWidths[activeResultTab]) {
        const timer = setTimeout(() => {
          const activeContainer = resultTableRefs.current[activeResultTab];
          
          if (activeContainer && activeContainer.offsetWidth > 0) {
            const activeResult = results.find(r => r.__id === activeResultTab);
            
            if (activeResult && activeResult.status === 200 && activeResult.body?.data?.length > 0) {
              const refWithWidth = { current: activeContainer };
              const widths = util.calculateColumnWidths(activeResult.body.data, refWithWidth);
              
              setResultColumnWidths(prev => ({
                ...prev,
                [activeResultTab]: widths
              }));
            }
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [activeResultTab, results, resultColumnWidths]);

  useEffect(() => {
    if (results && results.length > 1 && activeResultTab) {
      const queryResult = results.find(r => r.__id === activeResultTab);
      if (queryResult && queryResult.queryPosition) {
        highlightQueryInEditor(queryResult.queryPosition, activeKey);
      }
    } else if (results && results.length === 1 && results[0].queryPosition) {
      highlightQueryInEditor(results[0].queryPosition, activeKey);
    }
  }, [activeResultTab, results, activeKey]);

  useEffect(() => {
    setSortColumn(null);
    setSortType(null);
  }, [activeResultTab, results, result]);

  const getSortedData = (data, column, type) => {
    if (!Array.isArray(data) || !column || !type) return data || [];

    const direction = type === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const xRaw = a?.[column];
      const yRaw = b?.[column];

      const normalize = (v) => {
        if (v == null) return { kind: "null", value: null };
        if (typeof v === "number") return { kind: "number", value: v };
        if (typeof v === "boolean") return { kind: "boolean", value: v ? 1 : 0 };
        if (v instanceof Date) return { kind: "number", value: v.getTime() };
        if (typeof v === "string") {
          const num = Number(v);
          if (v.trim() !== "" && Number.isFinite(num)) return { kind: "number", value: num };
          return { kind: "string", value: v };
        }
        try {
          return { kind: "string", value: JSON.stringify(v) };
        } catch {
          return { kind: "string", value: String(v) };
        }
      };

      const x = normalize(xRaw);
      const y = normalize(yRaw);

      if (x.kind === "null" && y.kind === "null") return 0;
      if (x.kind === "null") return 1;
      if (y.kind === "null") return -1;

      if (x.kind === "number" && y.kind === "number") {
        return (x.value - y.value) * direction;
      }

      return String(x.value).localeCompare(String(y.value)) * direction;
    });
  };

  const paginatedResult = (() => {
    if (results && results.length > 0) {
      const activeResult = results.find(r => r.__id === activeResultTab);
      
      if (
        activeResult &&
        activeResult.status === 200 &&
        activeResult.body &&
        activeResult.body.data
      ) {
        const data = getSortedData(activeResult.body.data, sortColumn, sortType);
        const pagination = resultTabPagination[activeResultTab] || { page: 1, limit: 30 };
        return data.slice(
          (pagination.page - 1) * pagination.limit,
          pagination.page * pagination.limit
        );
      }
      return [];
    } else if (result) {
      const data = getSortedData(result, sortColumn, sortType);
      return data.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );
    }
    return [];
  })();

  const handleCloseResultTab = (closingId) => {
    setResults((prevResults) => {
      if (!prevResults) return null;
      
      const updatedResults = prevResults.filter(r => r.__id !== closingId);
      
      if (updatedResults.length === 0) {
        setResult(null);
        setResultTabPagination({});
        setActiveResultTab(null);
        setResultColumnWidths({});
        setCurrentPage(1);
        setRowsPerPage(30);
        return null;
      }
      
      const newActiveId = (activeResultTab === closingId || !updatedResults.find(r => r.__id === activeResultTab))
        ? updatedResults[0].__id
        : activeResultTab;
      
      setActiveResultTab(newActiveId);
      
      setResultTabPagination(currentPag => {
        const pagination = currentPag[newActiveId] || { page: 1, limit: 30 };
        setCurrentPage(pagination.page);
        setRowsPerPage(pagination.limit);
        
        const updated = { ...currentPag };
        delete updated[closingId];
        return updated;
      });
      
      return updatedResults;
    });
  };

  useEffect(() => {
    saveUserSession();
  }, []);

  useEffect(() => {
    const hasQueries = tabs.some(
      (tab) => tab.content !== tab.existingContent && tab.content.trim() !== ""
    );
    setHasTabsWithQueries(hasQueries);
  }, [tabs, setHasTabsWithQueries]);

  const highlightQueryInEditor = (queryPosition, activeTabKey) => {
    if (!queryPosition || !editorRefs.current[activeTabKey]) {
      return;
    }

    const editor = editorRefs.current[activeTabKey];
    const { startPos, endPos } = queryPosition;

    const model = editor.getModel();
    if (model) {
      const startPosition = model.getPositionAt(startPos);
      const endPosition = model.getPositionAt(endPos);

      editor.setSelection({
        startLineNumber: startPosition.lineNumber,
        startColumn: startPosition.column,
        endLineNumber: endPosition.lineNumber,
        endColumn: endPosition.column,
      });

      editor.focus();
      editor.revealRangeInCenter({
        startLineNumber: startPosition.lineNumber,
        startColumn: startPosition.column,
        endLineNumber: endPosition.lineNumber,
        endColumn: endPosition.column,
      });
    }
  };

  const tableRef = useRef(null);
  const resultTableRefs = useRef({});
  const resultsPaneRef = useRef(null);
  const [resultsPaneHeight, setResultsPaneHeight] = useState(0);

  const RESULTS_HEADER_HEIGHT = 44;
  const RESULTS_PAGINATION_HEIGHT = 24;
  const resultsTableHeight = Math.max(
    120,
    resultsPaneHeight - RESULTS_HEADER_HEIGHT - RESULTS_PAGINATION_HEIGHT
  );

  const MULTI_QUERY_TABLE_HEIGHT_OFFSET = 40;
  const multiQueryResultsTableHeight = Math.max(
    120,
    resultsTableHeight - MULTI_QUERY_TABLE_HEIGHT_OFFSET
  );

  useEffect(() => {
    if (!(result || results || error)) return;

    const el = resultsPaneRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      setResultsPaneHeight(h);
    };

    update();

    const ResizeObserverCtor = typeof ResizeObserver !== "undefined" ? ResizeObserver : null;
    const ro = ResizeObserverCtor ? new ResizeObserverCtor(update) : null;
    if (ro) ro.observe(el);

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [result, results, error]);

  const resultTabItems = useMemo(() => {
    if (!results) return [];
    
    return results.map((queryResult, index) => {
      const resultId = queryResult.__id;
      const isSuccess = queryResult.status === 200;
      const isError = queryResult.status !== 200;
      const tabTitle = `Query ${index + 1}`;

      return {
        key: resultId,
        label: (
          <span
            className={
              isError
                ? "query-tab-error"
                : "query-tab-success"
            }
          >
            {tabTitle}
          </span>
        ),
        className: isError ? "error-tab" : "success-tab",
        children: isSuccess ? (
          <div>
            <div className="query-results-header">
              {queryResult.body.statistics ? (
                <div className="result-stats">
                  <div className="elapsed-time">
                    Elapsed:{" "}
                    {queryResult.body.statistics.elapsed.toFixed(3)}s
                  </div>
                  <div>
                    Read:{" "}
                    {queryResult.body.statistics.rows_read}{" "}
                    rows (
                    {util.formatBytes(
                      Number(queryResult.body.statistics.bytes_read)
                    )}
                    )
                  </div>
                </div>
              ) : (
                <div>No statistics available</div>
              )}
              <div className="query-results-icons">
                <ExportButton result={queryResult.body.data} />
                <CloseIcon
                  style={{ cursor: "pointer" }}
                  onClick={() => handleCloseResultTab(resultId)}
                  data-testid="close-results"
                />
              </div>
            </div>
            <div
              ref={(el) => { resultTableRefs.current[resultId] = el; }}
              style={{ width: "100%" }}
              key={`table-container-${resultId}`}
            >
              <Table
                key={`table-${resultId}`}
                data={(() => {
                  const data = getSortedData(queryResult.body.data || [], sortColumn, sortType);
                  const pagination = resultTabPagination[resultId];
                  if (!pagination) return data.slice(0, 30);
                  
                  const start = (pagination.page - 1) * pagination.limit;
                  const end = start + pagination.limit;
                  return data.slice(start, end);
                })()}
                bordered
                cellBordered
                showHeader
                height={multiQueryResultsTableHeight}
                loading={loading}
                rowHeight={28}
                shouldUpdateScroll={false}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={(column, type) => {
                  setSortColumn(column);
                  setSortType(type);
                }}
              >
                {(() => {
                  if (!queryResult.body.data?.length) return null;
                  
                  const columnWidths = resultColumnWidths[resultId];
                  
                  if (!columnWidths) {
                    return null;
                  }
                  
                  return columnWidths.map(
                      (
                        {
                          key,
                          columnWidth,
                          minimumColumnWidth,
                        },
                        idx
                      ) => (
                        <Column
                          key={`${resultId}-${key}-${idx}`}
                          width={columnWidth}
                          minWidth={minimumColumnWidth}
                          align="center"
                          fullText
                          resizable
                          sortable
                        >
                          <HeaderCell>{key}</HeaderCell>
                          <Cell dataKey={key}>
                            {(rowData) => {
                              const cellData = rowData[key];
                              return typeof cellData === "object"
                                ? JSON.stringify(cellData)
                                : cellData;
                            }}
                          </Cell>
                        </Column>
                      )
                    );
                })()}
              </Table>
            </div>
            <Pagination
              prev
              next
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="xs"
              layout={["total", "-", "limit", "|", "pager", "skip"]}
              total={queryResult.body.data ? queryResult.body.data.length : 0}
              limitOptions={[10, 20, 30, 50]}
              limit={(resultTabPagination[resultId] || { page: 1, limit: 30 }).limit}
              activePage={(resultTabPagination[resultId] || { page: 1, limit: 30 }).page}
              onChangePage={(page) => {
                setResultTabPagination(prev => ({
                  ...prev,
                  [resultId]: { ...prev[resultId], page, limit: prev[resultId]?.limit || 30 }
                }));
                if (activeResultTab === resultId) {
                  setCurrentPage(page);
                }
              }}
              onChangeLimit={(limit) => {
                setResultTabPagination(prev => ({
                  ...prev,
                  [resultId]: { page: 1, limit }
                }));
                if (activeResultTab === resultId) {
                  setRowsPerPage(limit);
                  setCurrentPage(1);
                }
              }}
            />
          </div>
        ) : (
          <ErrorMessage
            queryResult={queryResult}
            error={error}
            onClose={() => handleCloseResultTab(resultId)}
          />
        ),
      };
    });
  }, [results, loading, resultsHeight, error, resultTabPagination, activeResultTab, setResultTabPagination, setCurrentPage, setRowsPerPage, handleCloseResultTab, resultColumnWidths, sortColumn, sortType]);

  return (
    <div className="querypageContainer" data-testid="query-editor-page">
      <div className="querypageMainContainer">
        <div className="querypageTitleContainer">
          Query Editor
          <div className="toggle-bar">
            <div
              role="button"
              data-testid="sidebar-left"
              onClick={() =>
                util.handleLeftClick(
                  activeIconLeft,
                  setActiveIconLeft,
                  isToggled,
                  setIsToggled
                )
              }
              className="sidebar-buttons"
            >
              <SidebarLeft isActive={activeIconLeft === "left"} />
            </div>
            <div
              role="button"
              data-testid="sidebar-down"
              onClick={() =>
                util.handleDownClick(
                  setIsResultVisible,
                  setIsRotated,
                  setActiveIconDown
                )
              }
              className="sidebar-buttons"
            >
              <SidebarDown isActive={activeIconDown === "down"} />
            </div>
            <div
              role="button"
              className="sidebar-buttons"
              data-testid="sidebar-right"
              onClick={() =>
                util.handleRightClick(
                  activeIconRight,
                  setActiveIconRight,
                  isRightToggled,
                  setIsRightToggled
                )
              }
            >
              <SidebarRight isActive={activeIconRight === "right"} />
            </div>
          </div>
        </div>
        <Progress.Line
          percent={progress}
          status="active"
          showInfo={false}
          strokeColor="#525383"
          className="query-editor-progress-bar"
        />
      </div>
      <div className="one">
        {isToggled && (
          <div className="two" style={{ width: sidebarWidth, minWidth: 275, maxWidth: 475 }}>
            <div className="dspicker-container">
              <SelectPicker
                className="sideTabDsPicker"
                data={datasources.map((ds) => {
                  const isInaccessible =
                    Array.isArray(noAccessDS) && noAccessDS.includes(ds.value);
                  const isSpecialDatasource = ["videotron", "rogers"].includes(
                    ds.value.toLowerCase()
                  );

                  const tooltipContent = isSpecialDatasource ? (
                    "You do not have access to this datasource."
                  ) : (
                    <>
                      You do not have access to this datasource. To gain access,
                      visit:{" "}
                      <a
                        href="https://etwiki.sys.comcast.net/display/maple/Gain+Access+to+Maple+UI"
                        data-testid="access-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        role="link"
                        aria-label="Gain Access to Maple UI"
                        style={{ color: "white", textDecoration: "underline" }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        https://etwiki.sys.comcast.net/display/maple/Gain+Access+to+Maple+UI
                      </a>
                    </>
                  );

                  return {
                    ...ds,
                    label: isInaccessible ? (
                      <Whisper
                        placement="top"
                        trigger="hover"
                        speaker={<Tooltip>{tooltipContent}</Tooltip>}
                        delay={1000}
                      >
                        <span style={{ color: "gray" }}>{ds.label}</span>
                      </Whisper>
                    ) : (
                      <span>{ds.label}</span>
                    ),
                  };
                })}
                style={{ width: 198 }}
                placeholder="Datasource"
                data-testid="datasource-select-picker"
                onSelect={(value) => {
                  setHasUserClearedDatasource(false);
                  localStorage.setItem(`maple_preferred_ds_${userId}`, value);
                  const isQueryEditorPanel = false
                  util.handleDatasourceSelect(
                    value,
                    setTableTrees,
                    setSelectedDatasource,
                    setEditorPanelDatasource,
                    isQueryEditorPanel,
                    setTablesCount,
                    progress,
                    setProgress,
                    tabs,
                    setTabs,
                    activeKey,
                    setDictionaries,
                    setDatasourceTablesMap
                  )
                }}
                value={selectedDatasource}
                onClean={() => {
                  setTableTrees([]);
                  setTablesCount(0);
                  setSelectedDatasource(null);
                  setDictionaries([]);
                  setHasUserClearedDatasource(true);
                }}
                disabledItemValues={noAccessDS || []}
              />
            </div>
            <Tabs defaultActiveKey="1" appearance="subtle">
              <Tabs.Tab eventKey="1" title="Tables">
                <div className="tables-header">
                  <span>Tables ({tablesCount})</span>
                  <Whisper
                    placement="right"
                    trigger="hover"
                    speaker={
                      <Tooltip className="table-legend-tooltip">
                        <div className="table-legend-content">
                          <div className="table-legend-item">
                            <span className="legend-dot legend-dot-dictionary"></span>
                            <span>Dictionary</span>
                          </div>
                          <div className="table-legend-item">
                            <span className="legend-dot legend-dot-view"></span>
                            <span>View</span>
                          </div>
                        </div>
                      </Tooltip>
                    }
                  >
                    <InfoOutlineIcon className="tables-info-icon" />
                  </Whisper>
                </div>
                {tableTrees.length === 0 ? (
                  <div>
                    <span className="no-tables-text">
                      Select a Datasource to view Tables
                    </span>
                  </div>
                ) : (
                  <div className="table-tree-list">
                    <Tree
                      key={selectedDatasource || "no-ds"}
                      className="datasource-tree"
                      data={tableTrees}
                      showIndentLine
                      renderTreeNode={(node) => {
                        const isTableNode = node.value.startsWith("table-");
                        const fullLabel = typeof node.label === "string" ? node.label : "";
                        const isDictionary =
                          node.isDictionary || dictionaries.includes(node.label);
                        const isView = node.isView || false;

                        if (!isTableNode) {
                          return <div>{node.label}</div>;
                        }

                        return (
                          <Whisper
                            placement="top"
                            trigger="hover"
                            speaker={<Tooltip>{tableTooltipText()}</Tooltip>}
                            delay={1000}
                          >
                            <div
                              className={`table-tree-node${isDictionary ? " dictionary-table" : isView ? " view-table" : ""}`}
                              title={fullLabel}
                            >
                              <TableIcon />
                              <span className="table-name-text">
                                {fullLabel}
                              </span>
                            </div>
                          </Whisper>
                        );
                      }}
                      onExpand={(expandedNodes, activeNode) => {
                        if (
                          activeNode &&
                          (!activeNode.children ||
                            activeNode.children.length === 0)
                        ) {
                          util.handleTableDropdown(
                            activeNode,
                            selectedDatasource,
                            tableTrees,
                            setTableTrees,
                            progress,
                            setProgress,
                            toaster
                          );
                        }
                      }}
                      onSelect={(activeNode, value, event) =>
                        util.handleNodeClick(
                          activeNode,
                          event,
                          clickTimeout,
                          selectedDatasource,
                          setEditorPanelDatasource,
                          tabs,
                          setTabs,
                          setActiveKey,
                          setSelectedTable,
                          setSelectedTableDatasource,
                          panelOpen,
                          setPanelOpen,
                          setQueryHistoryOpen,
                          setResult,
                          setResults,
                          setError,
                          setEditorContent,
                          setResultStats,
                          setLoading,
                          setActiveIconDown,
                          setActiveIconRight,
                          isRightToggled,
                          setIsRightToggled,
                          progress,
                          setProgress,
                          cancelTokenSource,
                          setCancelTokenSource,
                          toaster
                        )
                      }
                    />
                  </div>
                )}
              </Tabs.Tab>
              <Tabs.Tab eventKey="2" title="Queries">
                <QueryTree
                  userId={userId}
                  onSavedQueryClick={(title, queryContent, datasource) =>
                    util.handleSavedQueryClick(
                      title,
                      queryContent,
                      setTabs,
                      setActiveKey,
                      setEditorPanelDatasource,
                      datasource
                    )
                  }
                  setQueryHistoryOpen={setQueryHistoryOpen}
                  setPanelOpen={setPanelOpen}
                  setIsRightToggled={setIsRightToggled}
                  queryHistory={queryHistory}
                  setQueryHistory={setQueryHistory}
                  setQueryHistoryLoading={setQueryHistoryLoading}
                  setActiveIconRight={setActiveIconRight}
                  refreshSavedQueriesRef={refreshSavedQueriesRef}
                  datasources={datasources}
                />
              </Tabs.Tab>
            </Tabs>
          </div>
        )}
        {isToggled && (
          <div
            className="sidebar-resize-handle"
            onMouseDown={(e) => {
              e.preventDefault();
              isResizing.current = true;
              document.body.style.cursor = 'col-resize';
              document.body.style.userSelect = 'none';
            }}
          />
        )}
        <div className="three">
          <Allotment defaultSizes={[70, 30]} separator>
            <Allotment.Pane minSize={300}>
              <Allotment
                vertical
                separator
                defaultSizes={[70, 30]}
              >
                <Allotment.Pane minSize={250}>
                  <AntTabs
                    type="editable-card"
                    activeKey={activeKey}
                    onChange={(key) =>
                      util.handleChange(key, setActiveKey, tabs, setEditorPanelDatasource)
                    }
                    className="editor-tabs-title"
                    onEdit={(targetKey, action) => {
                      if (action === "add")
                        util.addTab(
                          tabs,
                          setTabs,
                          setActiveKey,
                          "",
                          selectedDatasource,
                          setEditorPanelDatasource
                        );
                      else {
                        const tabToClose = tabs.find((t) => t.key === targetKey);
                        const hasUnsavedChanges = tabToClose &&
                          !tabToClose.isSavedQuery &&
                          tabToClose.content && tabToClose.content.trim() !== "" &&
                          tabToClose.content.replace(/\n/g, "").trim() !== tabToClose.existingContent;
                        if (tabToClose?.isSavedQuery && tabToClose.content.replace(/\n/g, "").trim() !== tabToClose.existingContent) {
                          util.saveQuery(tabToClose, setTabs).then(() => {
                            if (refreshSavedQueriesRef.current) {
                              refreshSavedQueriesRef.current();
                            }
                          });
                        }
                        if (hasUnsavedChanges) {
                          setTabToCloseKey(targetKey);
                          setShowCloseTabModal(true);
                          return;
                        }
                        util.removeTab(
                          tabs,
                          setTabs,
                          activeKey,
                          setActiveKey,
                          targetKey
                        );
                      }
                    }}
                    items={tabs.map((tab) => ({
                      key: tab.key,
                      label: (
                        <span className="editor-title-tabs-span" title={tab.title}>
                          {tab.title.length > 20
                            ? `${tab.title.slice(0, 10)}...`
                            : tab.title}
                        </span>
                      ),
                      children: (
                        <div>
                          {tab.children ? (
                            tab.children
                          ) : (
                            <div>
                              <div className="query-title-bar">
                                <div className="query-title">
                                  <InputGroup inside>
                                    <Input
                                      className="editor-title-input"
                                      placeholder={tab.title}
                                      value={tab.title}
                                      onChange={(value) => {
                                        setTabs((prevTabs) =>
                                          prevTabs.map((t) =>
                                            t.key === tab.key
                                              ? { ...t, title: value }
                                              : t
                                          )
                                        );
                                      }}
                                    />
                                    <InputGroup.Addon>
                                      <EditIcon />
                                    </InputGroup.Addon>
                                  </InputGroup>
                                </div>
                                <div className="query-title-buttons">
                                  <SelectPicker
                                    className="datasourcePicker"
                                    data={datasources}
                                    style={{ width: 180 }}
                                    placeholder="Datasource"
                                    data-testid="datasource-select-picker-two"
                                    onSelect={(value) => {
                                      setHasUserClearedDatasource(false);
                                      const isQueryEditorPanel = true;
                                      util.handleDatasourceSelect(
                                        value,
                                        setTableTrees,
                                        setSelectedDatasource,
                                        setEditorPanelDatasource,
                                        isQueryEditorPanel,
                                        setTablesCount,
                                        progress,
                                        setProgress,
                                        tabs,
                                        setTabs,
                                        activeKey,
                                        setDictionaries,
                                        setDatasourceTablesMap
                                      );
                                    }}
                                    value={editorPanelDatasource}
                                    onClean={() => {
                                      setEditorPanelDatasource(null);
                                      setHasUserClearedDatasource(true);
                                      editorPanelDatasourceRef.current = null;
                                    }}
                                    disabledItemValues={noAccessDS}
                                  />
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={<Tooltip>{getTooltipText()}</Tooltip>}
                                    delay={500}
                                  >
                                    <IconButton
                                      size="sm"
                                      className="query-buttons"
                                      icon={
                                        loading ? (
                                          <CloseIcon
                                            style={{ backgroundColor: "#df5b5c" }}
                                          />
                                        ) : (
                                          <PlayOutlineIcon />
                                        )
                                      }
                                      style={{
                                        backgroundColor: loading
                                          ? "#df5b5c"
                                          : "#d8d8ed",
                                      }}
                                      onClick={() =>
                                        util.runQuery(
                                          tab.key,
                                          editorRefs,
                                          loading,
                                          setLoading,
                                          setResult,
                                          setError,
                                          editorPanelDatasource,
                                          setEditorHeight,
                                          setResultStats,
                                          setActiveIconDown,
                                          progress,
                                          setProgress,
                                          cancelTokenSource,
                                          setCancelTokenSource,
                                          setResults,
                                          setActiveResultTab,
                                          false,
                                          queryHistoryRef
                                        )
                                      }
                                      data-testid="run-query-button"
                                    >
                                      {loading ? "Cancel" : "Run"}
                                    </IconButton>
                                  </Whisper>
                                  <IconButton
                                    size="sm"
                                    className="query-buttons"
                                    icon={<SaveIcon />}
                                    onClick={() => setShowSaveModal(true)}
                                    data-testid="save-query-button"
                                  >
                                    Save
                                  </IconButton>
                                </div>
                              </div>
                              <div style={{ flex: 1, minHeight: 0 }}>
                              <Editor
                                height="100%"
                                defaultLanguage="sql"
                                theme={isDarkMode ? "vs-dark" : "vs-light"}
                                data-testid="monaco-editor"
                                className="query-editor"
                                value={tab.content}
                                options={{ automaticLayout: true }}
                                onMount={(editor, monaco) =>
                                  util.handleEditorDidMount(
                                    tab.key,
                                    editor,
                                    editorRefs,
                                    loading,
                                    setLoading,
                                    setResult,
                                    setError,
                                    editorPanelDatasourceRef,
                                    setEditorHeight,
                                    setResultStats,
                                    setActiveIconDown,
                                    progress,
                                    setProgress,
                                    cancelTokenSource,
                                    setCancelTokenSource,
                                    queryHistoryRef,
                                    monaco,
                                    setResults,
                                    setActiveResultTab,
                                    datasourceTablesMapRef
                                  )
                                }
                                onChange={(newValue) => {
                                  setTabs((prevTabs) =>
                                    prevTabs.map((tab) =>
                                      tab.key === activeKey
                                        ? { ...tab, content: newValue }
                                        : tab
                                    )
                                  );
                                }}
                              />
                              </div>
                            </div>
                          )}
                        </div>
                      ),
                    }))}
                  ></AntTabs>
                </Allotment.Pane>

                {(result || results || error) && (
                  <Allotment.Pane minSize={120} visible={isResultVisible}>
                    <div
                      className="query-results"
                      data-testid="query-result"
                      ref={resultsPaneRef}
                      style={{ height: "100%" }}
                    >
                      {results && (results.length > 1 || isMultiQuery) ? (
                        <div
                          className="multi-query-results"
                          style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                          }}
                        >
                          <AntTabs
                            type="card"
                            activeKey={activeResultTab}
                            destroyInactiveTabPane={false}
                            onChange={(resultId) => {
                              setActiveResultTab(resultId);

                              const pagination =
                                resultTabPagination[resultId] || {
                                  page: 1,
                                  limit: 30,
                                };
                              setCurrentPage(pagination.page);
                              setRowsPerPage(pagination.limit);
                            }}
                            items={resultTabItems}
                            style={{ flex: 1, minHeight: 0 }}
                          />
                        </div>
                      ) : result || (results && results.length === 1) ? (
                        <>
                          {results &&
                          results.length === 1 &&
                          results[0].status !== 200 ? (
                            <ErrorMessage
                              queryResult={results?.[0]}
                              error={error}
                              onClose={() => {
                                setResult(null);
                                setResults(null);
                                setError(null);
                              }}
                            />
                          ) : (
                            <>
                              <div className="query-results-header">
                                {(() => {
                                  const stats =
                                    results && results.length === 1
                                      ? results[0].body?.statistics
                                      : resultStats;
                                  return stats ? (
                                    <div className="result-stats">
                                      <div className="elapsed-time">
                                        Elapsed: {stats.elapsed.toFixed(3)}s
                                      </div>
                                      <div>
                                        Read: {stats.rows_read} rows (
                                        {util.formatBytes(Number(stats.bytes_read))}
                                        )
                                      </div>
                                    </div>
                                  ) : (
                                    <div>No statistics available</div>
                                  );
                                })()}
                                <div className="query-results-icons">
                                  <ExportButton
                                    result={
                                      results && results.length === 1
                                        ? results[0].body.data
                                        : result
                                    }
                                  />
                                  <ArrowDownLineIcon
                                    style={{
                                      fontSize: "1em",
                                      transform: isRotated
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 1s ease",
                                    }}
                                    color="blue"
                                    onClick={() =>
                                      util.handleDownClick(
                                        setIsResultVisible,
                                        setIsRotated,
                                        setActiveIconDown
                                      )
                                    }
                                    data-testid="collapse-results"
                                  />
                                  <CloseIcon
                                    style={{ fontSize: "1em" }}
                                    onClick={() => {
                                      setResult(null);
                                      setResults(null);
                                    }}
                                    data-testid="close-results"
                                  />
                                </div>
                              </div>
                              <div ref={tableRef} style={{ width: "100%" }}>
                                <Table
                                  data={paginatedResult}
                                  bordered
                                  cellBordered
                                  showHeader
                                  height={resultsTableHeight}
                                  shouldUpdateScroll={false}
                                  loading={loading}
                                  rowHeight={28}
                                  sortColumn={sortColumn}
                                  sortType={sortType}
                                  onSortColumn={(column, type) => {
                                    setSortColumn(column);
                                    setSortType(type);
                                  }}
                                >
                                  {(() => {
                                    const dataForColumns =
                                      results && results.length === 1
                                        ? results[0].body.data
                                        : result;
                                    return (
                                      dataForColumns &&
                                      dataForColumns.length > 0 &&
                                      util
                                        .calculateColumnWidths(dataForColumns, tableRef)
                                        .map(
                                          (
                                            { key, columnWidth, minimumColumnWidth },
                                            index
                                          ) => (
                                            <Column
                                              key={`${key}-${index}`}
                                              width={columnWidth}
                                              minWidth={minimumColumnWidth}
                                              align="center"
                                              fullText
                                              resizable
                                              sortable
                                            >
                                              <HeaderCell>{key}</HeaderCell>
                                              <Cell dataKey={key}>
                                                {(rowData) => {
                                                  const cellData = rowData[key];
                                                  return typeof cellData === "object"
                                                    ? JSON.stringify(cellData)
                                                    : cellData;
                                                }}
                                              </Cell>
                                            </Column>
                                          )
                                        )
                                    );
                                  })()}
                                </Table>
                              </div>
                              <Pagination
                                prev
                                next
                                ellipsis
                                boundaryLinks
                                maxButtons={5}
                                size="xs"
                                layout={["total", "-", "limit", "|", "pager", "skip"]}
                                total={(() => {
                                  const dataForTotal =
                                    results && results.length === 1
                                      ? results[0].body.data
                                      : result;
                                  return dataForTotal ? dataForTotal.length : 0;
                                })()}
                                limitOptions={[10, 20, 30, 50]}
                                limit={rowsPerPage}
                                activePage={currentPage}
                                onChangePage={(page) => {
                                  setCurrentPage(page);
                                  if (results && results.length === 1) {
                                    setResultTabPagination((prev) => ({
                                      ...prev,
                                      [results[0].__id]: { page, limit: rowsPerPage },
                                    }));
                                  }
                                }}
                                onChangeLimit={(value) => {
                                  setRowsPerPage(value);
                                  setCurrentPage(1);
                                  if (results && results.length === 1) {
                                    setResultTabPagination((prev) => ({
                                      ...prev,
                                      [results[0].__id]: { page: 1, limit: value },
                                    }));
                                  }
                                }}
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <div className="query-error-result">
                          <Message showIcon type="error" header="Error">
                            <div className="error-header">
                              {isWafError(error) ? (
                                <WafErrorContent message={error} />
                              ) : (
                                <strong>{error}</strong>
                              )}
                              <CloseIcon
                                className="close-error-icon"
                                onClick={() => setError(null)}
                                data-testid="close-error-message"
                              />
                            </div>
                          </Message>
                        </div>
                      )}
                    </div>
                  </Allotment.Pane>
                )}
              </Allotment>
            </Allotment.Pane>

            <Allotment.Pane minSize={550} maxSize={700} visible={isRightToggled && (panelOpen || queryHistoryOpen)}>
              {panelOpen ? (
                <div className="right-panel">
                  <div className="panel-container">
                    <div className="panel-header">
                      <h5 className="panel-title">
                        Table Details:
                        {selectedTable ? (
                          <span style={{ color: "#df5b5c", marginLeft: 6 }}>
                            {selectedTable}
                          </span>
                        ) : (
                          ""
                        )}
                      </h5>
                    </div>
                    <div className="panel-body">
                      <Tabs defaultActiveKey="1" appearance="subtle">
                        <Tabs.Tab eventKey="1" title="Table Info">
                          <div className="table-properties-container">
                            <util.PropertiesBlockComponent
                              data-testid="properties-block-component"
                              datasource={selectedTableDatasource}
                              table={selectedTable}
                            />
                            <util.CodeBlockComponent
                              language="sql"
                              datasource={selectedTableDatasource}
                              table={selectedTable}
                            />
                          </div>
                        </Tabs.Tab>
                        <Tabs.Tab eventKey="2" title="Column Info">
                          <div className="column-info-container">
                            <util.ColumnPropsComponent
                              datasource={selectedTableDatasource}
                              table={selectedTable}
                            />
                          </div>
                        </Tabs.Tab>
                      </Tabs>
                    </div>
                  </div>
                </div>
              ) : queryHistoryOpen ? (
                <QueryHistoryPanel
                  userId={userId}
                  queryHistory={queryHistory}
                  setQueryHistory={setQueryHistory}
                  setQueryHistoryOpen={setQueryHistoryOpen}
                  setActiveIconRight={setActiveIconRight}
                  queryHistoryLoading={queryHistoryLoading}
                />
              ) : null}
            </Allotment.Pane>
          </Allotment>
        </div>
      </div>

      <Modal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        className="save-query-modal"
      >
        <Modal.Header>
          <Modal.Title>Save Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const activeTab = tabs.find((tab) => tab.key === activeKey) || {};
            return (
              <InputGroup>
                <Input
                  className="editor-title-input"
                  placeholder="Query Title"
                  value={activeTab.title || ""}
                  onChange={(value) =>
                    setTabs((prevTabs) =>
                      prevTabs.map((tab) =>
                        tab.key === activeKey ? { ...tab, title: value } : tab
                      )
                    )
                  }
                />
                <InputGroup.Addon>
                  <EditIcon />
                </InputGroup.Addon>
              </InputGroup>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowSaveModal(false)}
            appearance="subtle"
            data-testid="cancel-save-query"
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={async () => {
              await util.handleSaveQuery(
                tabs,
                setTabs,
                activeKey,
                setShowSaveModal,
                toaster
              );
              if (refreshSavedQueriesRef.current) {
                refreshSavedQueriesRef.current();
              }
            }}
            data-testid="save-query-modal-button"
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={showCloseTabModal}
        onClose={() => setShowCloseTabModal(false)}
        data-testid="close-tab-modal"
      >
        <Modal.Header>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes in this tab. Are you sure you want to close it?
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowCloseTabModal(false)}
            appearance="subtle"
            data-testid="cancel-close-tab"
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            color="red"
            onClick={() => {
              const closedTab = tabs.find((t) => t.key === tabToCloseKey);
              setShowCloseTabModal(false);
              util.removeTab(
                tabs,
                setTabs,
                activeKey,
                setActiveKey,
                tabToCloseKey
              );
              setTabToCloseKey(null);
              toaster.push(
                <Message type="success" showIcon closable>
                  <strong>{closedTab?.title || "Tab"}</strong> closed
                </Message>,
                { placement: "topCenter", duration: 3000 }
              );
            }}
            data-testid="confirm-close-tab"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QueryEditorPage;
